import React, { lazy, memo, Suspense, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Route, Redirect, Switch } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';

import {
  FLEET as FLEET_ROUTE,
  MAINTENANCE_REPORT,
  MAP,
} from './Constants/Routes';
import AuthService from './Services/AuthService';
import {
  HEADER_HEIGHT,
  LARGE_WIDTH_PX,
  MOBILE_HEADER_HEIGHT,
} from './Styles/constants';

import Asset from './Pages/Asset';
const AssetModel = lazy(() => import('./Pages/AssetModel'));
const Events = lazy(() => import('./Pages/Events'));
const CsvReport = lazy(() => import('./Pages/CsvReport'));
const CsvReports = lazy(() => import('./Pages/CsvReports'));
const EngineApp = lazy(() => import('./Pages/EngineApp'));
const Fleet = lazy(() => import('./Pages/Fleet'));
const Geofence = lazy(() => import('./Pages/Geofence'));
const Header = lazy(() => import('./Components/Header/Header'));
import { Loading } from './Components/NoDataIndication';
const Login = lazy(() => import('./Pages/Login'));
const Maintenance = lazy(() => import('./Pages/Maintenance'));
const MaintenanceReport = lazy(() => import('./Pages/MaintenanceReport'));
const Map = lazy(() => import('./Pages/Map'));
import NotFound from './Pages/NotFound';
const Organization = lazy(() => import('./Pages/Organization'));
const CreateEditSchedule = lazy(
  () => import('./Components/Maintenance/CreateEditScheduleContainer')
);
const Search = lazy(() => import('./Pages/Search'));
const Settings = lazy(() => import('./Pages/Settings'));
import Sidebar from './Components/Organization/InventorySidebar';
const WaterPump = lazy(() => import('./Pages/WaterPump'));

export default memo(Routes);

function Routes(props) {
  const auth = useMemo(() => new AuthService(), []);
  const [isLoggedIn, setIsLoggedIn] = useState(!!auth.getToken());
  const {
    currentOrg,
    currentUser,
    handleLoginSuccess,
    handleSearchTerm,
    missingImage,
    nextOrgName,
    orgsMap,
    rootOrg,
    profileIcon,
    searchTerm,
    setCurrentOrg,
    showOverlay,
    sidebarCollapsed,
    updateCurrentUser,
    width,
  } = props;

  const isDesktop = useMemo(() => width >= LARGE_WIDTH_PX, [width]);

  return (
    <div
      className={`min-vw-100 min-vh-100 ${isLoggedIn ? 'app-container' : ''}`}
    >
      {/* sticky-top bg-white */}
      <div className={isLoggedIn ? 'header-container h-100' : 'd-none'}>
        <Suspense fallback={<Loading />}>
          <Header
            handleSearchTerm={handleSearchTerm}
            orgsMap={orgsMap}
            sideBar={<Sidebar orgsMap={orgsMap} />}
          />
        </Suspense>
      </div>
      <div className="switch-router-container position-relative">
        <Switch>
          <Route exact path="/">
            {isLoggedIn ? <Redirect to={MAP} /> : <Redirect to="/login" />}
          </Route>
          <Route path="/login">
            <Suspense fallback={<Loading />}>
              <Login
                handleLoginSuccess={handleLoginSuccess}
                isLoggedIn={isLoggedIn}
                setIsLoggedIn={setIsLoggedIn}
              />
            </Suspense>
          </Route>
          <Route path="/maintenance">
            <Suspense fallback={<Loading />}>
              <Maintenance />
            </Suspense>
          </Route>
          <Route path={MAINTENANCE_REPORT}>
            <Suspense fallback={<Loading />}>
              <MaintenanceReport currentOrg={currentOrg} width={width} />
            </Suspense>
          </Route>
          <Route path={MAP}>
            <Suspense fallback={<Loading />}>
              <Map
                currentOrg={currentOrg}
                handleSearchTerm={handleSearchTerm}
                searchTerm={searchTerm}
                width={width}
              />
            </Suspense>
          </Route>
          <Route path={['/fleet/:dashboardID', FLEET_ROUTE]}>
            <Suspense fallback={<Loading />}>
              <Fleet
                currentOrg={currentOrg}
                currentUser={currentUser}
                orgsMap={orgsMap}
                width={width}
              />
            </Suspense>
          </Route>
          <Route path="/organization">
            <Suspense fallback={<Loading />}>
              <Organization
                currentOrg={currentOrg}
                handleSearchTerm={handleSearchTerm}
                missingImage={missingImage}
                orgsMap={orgsMap}
                profileIcon={profileIcon}
                searchTerm={searchTerm}
                sidebarCollapsed={sidebarCollapsed}
                width={width}
              />
            </Suspense>
          </Route>
          <Route path="/events">
            <Suspense fallback={<Loading />}>
              <Events
                currentOrg={currentOrg}
                orgsMap={orgsMap}
                user={currentUser}
                width={width}
              />
            </Suspense>
          </Route>
          <Route exact path="/csv-reports">
            <Suspense fallback={<Loading />}>
              <CsvReports currentOrg={currentOrg} width={width} />
            </Suspense>
          </Route>
          <Route path="/csv-reports/:reportID">
            <Suspense fallback={<Loading />}>
              <CsvReport
                currentOrg={currentOrg}
                currentUser={currentUser}
                width={width}
              />
            </Suspense>
          </Route>
          <Route path="/asset/:assetID">
            <Suspense fallback={<Loading />}>
              {currentOrg?._id ? (
                <Asset />
              ) : (
                <div className="h-100 w-100 d-flex justify-content-center align-items-center">
                  <Loading />
                </div>
              )}
            </Suspense>
          </Route>
          <Route path="/asset-model/:templateID">
            <Suspense fallback={<Loading />}>
              <AssetModel
                currentOrg={currentOrg}
                orgsMap={orgsMap}
                setCurrentOrg={setCurrentOrg}
                width={width}
              />
            </Suspense>
          </Route>
          <Route path="/geofence/:geofenceID">
            <Suspense fallback={<Loading />}>
              <Geofence rootOrg={rootOrg} />
            </Suspense>
          </Route>
          <Route path={['/schedule/:scheduleId', '/schedule']}>
            <Suspense fallback={<Loading />}>
              <CreateEditSchedule
                currentOrg={currentOrg}
                currentUser={currentUser}
                rootOrg={rootOrg}
                setCurrentOrg={setCurrentOrg}
                width={width}
              />
            </Suspense>
          </Route>
          <Route path={['/watertransfer/:waterpumpId', '/watertransfer']}>
            <Suspense fallback={<Loading />}>
              <WaterPump />
            </Suspense>
          </Route>
          <Route path={['/engineanalysis/:assetID', '/engineanalysis']}>
            <Suspense fallback={<Loading />}>
              <EngineApp />
            </Suspense>
          </Route>
          <Route path="/search">
            <Suspense fallback={<Loading />}>
              <Search
                currentOrg={currentOrg}
                currentUser={currentUser}
                orgsMap={orgsMap}
                rootOrg={rootOrg}
                sidebarCollapsed={sidebarCollapsed}
                width={width}
              />
            </Suspense>
          </Route>
          <Route path="/settings">
            <Suspense fallback={<Loading />}>
              <Settings updateCurrentUser={updateCurrentUser} width={width} />
            </Suspense>
          </Route>
          <Route>
            <NotFound width={width} />
          </Route>
        </Switch>
      </div>
      {showOverlay ? (
        <div className="overlay text-white font-weight-light position-fixed d-flex flex-column justify-content-center align-items-center w-100 h-100">
          <FontAwesomeIcon icon={faCircleNotch} size="2x" spin />
          <span className="mt-3">{`Returning to ${nextOrgName}`}</span>
        </div>
      ) : null}
      <style jsx>{`
        .overlay {
          background-color: rgba(0, 0, 0, 0.5);
          bottom: 0;
          left: 0;
          right: 0;
          top: 0;
          z-index: 2000;
        }
        .app-container {
          display: grid;
          grid-template-areas: 'header' 'main';
          grid-template-columns: 1fr;
          grid-template-rows: ${isDesktop
              ? HEADER_HEIGHT
              : MOBILE_HEADER_HEIGHT} 1fr;
        }
        .header-container {
          grid-area: header;
        }
        .switch-router-container {
          grid-area: main;
        }
      `}</style>
    </div>
  );
}

Routes.propTypes = {
  currentOrg: PropTypes.object,
  currentUser: PropTypes.object,
  handleLoginSuccess: PropTypes.func,
  handleSearchTerm: PropTypes.func,
  missingImage: PropTypes.string,
  nextOrgName: PropTypes.string,
  orgsMap: PropTypes.object,
  profileIcon: PropTypes.string,
  resetStateOnLogout: PropTypes.func,
  rootOrg: PropTypes.object,
  searchTerm: PropTypes.string,
  setCurrentOrg: PropTypes.func,
  showOverlay: PropTypes.bool,
  sidebarCollapsed: PropTypes.bool,
  updateCurrentUser: PropTypes.func,
  width: PropTypes.number,
};
